import React, { useMemo, useRef, useState } from "react";
import { RCCAlert, RCCGrid } from "react-common-components";
import { IBarProps } from "react-common-components/dist/components/Chart/ComposedChart/interface";
import FadedDiv from "src/components/Animations/fade";
import ComposedChart from "src/components/Charts/ComposedChart";
import palettes from "src/components/PalettePicker/colors";
import { v4 } from "uuid";
import { IChartColorIndicators, IChartCompIndicators, IChartLabelIndicators, IChartTypeIndicator, IComment } from "../ModalIndicatorChart/interface";
import { IChartIndicatorsContainer } from "./interface";
import ListForChartModal from "./listForChartModal";

const ChartIndicatorsContainer: React.FC<IChartIndicatorsContainer> = ({
	configCharts,
	fieldsChart,
	fieldsChartAccumulated,
	onClickChartAreaBarLine,
	dataChart,
	dataChartAccumulated,
	dataTable
}) => {
	// Comment
	const timer = useRef<number>(0);
	const [commentAlert, setCommentAlert] = useState<IComment[] | undefined>(undefined);
	const mouseOverAreaBarLine = (e:any) => {
		timer.current = window.setTimeout(() => {
			setCommentAlert(e.comments);
		}, 300);
	};
	const mouseOutAreaBarLine = () => {
		window.clearTimeout(timer.current);
		setCommentAlert(undefined);
	};

	const colorPallete = palettes.find((pallete) => pallete.idColor === configCharts.iqc001Paletacores.toString())?.colors;
    
	const MemorizedChart = useMemo(() => {
		const yAxis = [];
		yAxis.push({
			label: configCharts.iqc001Roteixoesq === null ? undefined : { value: configCharts.iqc001Roteixoesq, position: "insideLeft", offset: 0, angle: -90 },
			domain: configCharts.iqc001Escalamin === null || configCharts.iqc001Escalamax === null ? [(dataMin: number) => dataMin > 0 ? 0 : dataMin, (dataMax: number) => dataMax < 0 ? 0 : Math.ceil(dataMax + 0.1 * dataMax)] : [configCharts.iqc001Escalamin, configCharts.iqc001Escalamax]
		});
		if(configCharts.iqc001Roteixodir !== null)
			yAxis.push({
				yAxisId: "right",
				orientation: "right",
				label: configCharts.iqc001Roteixodir === null ? undefined : { value: configCharts.iqc001Roteixodir, position: "insideRight", offset: 0, angle: 90 },
				domain: configCharts.iqc001Escalamindir === null || configCharts.iqc001Escalamaxdir === null ? [(dataMin: number) => dataMin > 0 ? 0 : dataMin, (dataMax: number) => dataMax < 0 ? 0 : Math.ceil(dataMax + 0.1 * dataMax)] : [configCharts.iqc001Escalamindir, configCharts.iqc001Escalamaxdir]
			} as any);

		const label: {
			position: string;
			angle: number;
		} = { 
			position: configCharts.iqc001Labelvalores === IChartLabelIndicators.HORIZONTAL ? "top" : "center", 
			angle: configCharts.iqc001Labelvalores === IChartLabelIndicators.HORIZONTAL ? 0 : 90
		};

		const areas = fieldsChart.filter((fields) => fields.chartType === IChartTypeIndicator.AREA).map((areaField) => {
			let labelArea: { position: string; angle: number; } | undefined = label;
			if(configCharts.showAll === false || (configCharts.showAll === undefined && areaField.showLabel !== true))
				labelArea = undefined;
			if(configCharts.showAll === true)
				labelArea = label;

			return{
				dataKey: areaField.dataKey,
				type: "monotone",
				colorIndex: areaField.colorIndex,
				onMouseEnter: mouseOverAreaBarLine,
				onMouseLeave: mouseOutAreaBarLine,
				label: labelArea,
				fill: areaField.fieldColor !== null ? areaField.fieldColor : undefined,
				stroke: areaField.fieldColor !== null ? areaField.fieldColor : undefined,
				onClick: (periodId: number) => onClickChartAreaBarLine && onClickChartAreaBarLine(periodId, false),
				yAxisId: areaField.axisRight ? "right" : undefined,
				decimalPlaces: areaField.decimalPlaces
			};
		});
			
		const bars = fieldsChart.filter((fields) => fields.chartType === IChartTypeIndicator.BAR).map((barField) => {
			let fieldColorAsMetrics: boolean | undefined;
			let labelBar: { position: string; angle: number; } | undefined = label;
			
			if(configCharts.iqc001Corgrafico === IChartColorIndicators.METRICS)
				fieldColorAsMetrics = fieldsChart.find((x) => x.dataKey === barField.dataKey)?.customColorForEachBarOfSameData;

			if(configCharts.showAll === false || (configCharts.showAll === undefined && barField.showLabel !== true))
				labelBar = undefined;
			if(configCharts.showAll === true)
				labelBar = label;

			const fill = barField.fieldColor !== null ? barField.fieldColor : undefined;
			const stroke =  barField.fieldColor !== null ? barField.fieldColor : undefined;

			return{
				dataKey: barField.dataKey,
				barSize: 24,
				label: labelBar,
				stackId: configCharts.iqc001Compgrafico === IChartCompIndicators.STACKED ? "stack" : undefined,
				customColorForEachBarOfSameData: fieldColorAsMetrics ? true : undefined,
				fill: fieldColorAsMetrics ? "#000" : fill,
				stroke: fieldColorAsMetrics ? "#000" : stroke,
				colorIndex: barField.colorIndex,
				onMouseEnter: mouseOverAreaBarLine,
				onMouseLeave: mouseOutAreaBarLine,
				onClick: (periodId: number) => onClickChartAreaBarLine && onClickChartAreaBarLine(periodId, false),
				yAxisId: barField.axisRight ? "right" : undefined,
				decimalPlaces: barField.decimalPlaces
			};
		});

		const lines = fieldsChart.filter((fields) => fields.chartType === IChartTypeIndicator.LINE).map((lineField) => {
			let labelLine: { position: string; angle: number; } | undefined = label;
			if(configCharts.showAll === false || (configCharts.showAll === undefined && lineField.showLabel !== true))
				labelLine = undefined;
			if(configCharts.showAll === true)
				labelLine = label;
				
			return{
				dataKey: lineField.dataKey,
				type: "monotone",
				colorIndex: lineField.colorIndex,
				onMouseEnter: mouseOverAreaBarLine,
				onMouseLeave: mouseOutAreaBarLine,
				label: labelLine,
				fill: lineField.fieldColor !== null ? lineField.fieldColor : undefined,
				stroke: lineField.fieldColor !== null ? lineField.fieldColor : undefined,
				onClick: (periodId: number) => onClickChartAreaBarLine && onClickChartAreaBarLine(periodId, false),
				yAxisId: lineField.axisRight ? "right" : undefined,
				decimalPlaces: lineField.decimalPlaces
			};
		});

		return (
			<ComposedChart 
				width={1200}
				height={400}
				data={dataChart}
				xAxis={{
					scale: "band"
				}}
				yAxis={yAxis}
				area={areas as any[]}
				bar={bars as IBarProps[]}
				line={lines as any[]}
				colorsPallete={colorPallete}
			/>
		);
	}, [dataChart, configCharts]);
	const MemorizedChartAccumulated = useMemo(() => {
		const yAxis = [];
		yAxis.push({
			label: configCharts.iqc001Roteixoesq === null ? undefined : { value: configCharts.iqc001Roteixoesq, position: "insideLeft", offset: 0, angle: -90 },
			domain: configCharts.iqc001Escalamin === null || configCharts.iqc001Escalamax === null ? [(dataMin: number) => dataMin > 0 ? 0 : dataMin, (dataMax: number) => dataMax < 0 ? 0 : Math.ceil(dataMax + 0.1 * dataMax)] : [configCharts.iqc001Escalamin, configCharts.iqc001Escalamax]
		});
		if(configCharts.iqc001Roteixodir !== null)
			yAxis.push({
				yAxisId: "right",
				orientation: "right",
				label: configCharts.iqc001Roteixodir === null ? undefined : { value: configCharts.iqc001Roteixodir, position: "insideRight", offset: 0, angle: 90 },
				domain: configCharts.iqc001Escalamindir === null || configCharts.iqc001Escalamaxdir === null ? [(dataMin: number) => dataMin > 0 ? 0 : dataMin, (dataMax: number) => dataMax < 0 ? 0 : Math.ceil(dataMax + 0.1 * dataMax)] : [configCharts.iqc001Escalamindir, configCharts.iqc001Escalamaxdir]
			} as any);

		const label: {
			position: string;
			angle: number;
		} = { 
			position: configCharts.iqc001Labelvalores === IChartLabelIndicators.HORIZONTAL ? "top" : "center", 
			angle: configCharts.iqc001Labelvalores === IChartLabelIndicators.HORIZONTAL ? 0 : 90
		};

		const areas = fieldsChartAccumulated.filter((fields) => fields.chartType === IChartTypeIndicator.AREA).map((areaField) => {
			let labelArea: { position: string; angle: number; } | undefined = label;
			if(configCharts.showAll === false || (configCharts.showAll === undefined && areaField.showLabel !== true))
				labelArea = undefined;
			if(configCharts.showAll === true)
				labelArea = label;

			return{
				dataKey: areaField.dataKey,
				type: "monotone",
				colorIndex: areaField.colorIndex,
				label: labelArea,
				fill: areaField.fieldColor !== null ? areaField.fieldColor : undefined,
				stroke: areaField.fieldColor !== null ? areaField.fieldColor : undefined,
				yAxisId: areaField.axisRight ? "right" : undefined,
				decimalPlaces: areaField.decimalPlaces
			};
		});

		const bars = fieldsChartAccumulated.filter((fields) => fields.chartType === IChartTypeIndicator.BAR).map((barField) => {
			let fieldColorAsMetrics: boolean | undefined;
			let labelBar: { position: string; angle: number; } | undefined = label;

			if(configCharts.iqc001Corgrafico === IChartColorIndicators.METRICS)
				fieldColorAsMetrics = fieldsChartAccumulated.find((x) => x.dataKey === barField.dataKey)?.customColorForEachBarOfSameData;

			if(configCharts.showAll === false || (configCharts.showAll === undefined && barField.showLabel !== true))
				labelBar = undefined;
			if(configCharts.showAll === true)
				labelBar = label;
				
			const fill = barField.fieldColor !== null ? barField.fieldColor : undefined;
			const stroke = barField.fieldColor !== null ? barField.fieldColor : undefined;

			return {
				dataKey: barField.dataKey,
				barSize: 24,
				label: labelBar,
				stackId: configCharts.iqc001Compgrafico === IChartCompIndicators.STACKED ? "stack" : undefined,
				customColorForEachBarOfSameData: fieldColorAsMetrics ? true : undefined,
				fill: fieldColorAsMetrics ? "#000" : fill,
				stroke: fieldColorAsMetrics ? "#000" : stroke,
				colorIndex: barField.colorIndex,
				yAxisId: barField.axisRight ? "right" : undefined,
				decimalPlaces: barField.decimalPlaces
			};
		});

		const lines = fieldsChartAccumulated.filter((fields) => fields.chartType === IChartTypeIndicator.LINE).map((lineField) => {
			let labelLine: { position: string; angle: number; } | undefined = label;
			if(configCharts.showAll === false || (configCharts.showAll === undefined && lineField.showLabel !== true))
				labelLine = undefined;
			if(configCharts.showAll === true)
				labelLine = label;

			return {
				dataKey: lineField.dataKey,
				type: "monotone",
				colorIndex: lineField.colorIndex,
				label: labelLine,
				fill: lineField.fieldColor !== null ? lineField.fieldColor : undefined,
				stroke: lineField.fieldColor !== null ? lineField.fieldColor : undefined,
				yAxisId: lineField.axisRight ? "right" : undefined,
				decimalPlaces: lineField.decimalPlaces
			};
		});

		return (
			<ComposedChart 
				width={600}
				height={400}
				data={dataChartAccumulated}
				xAxis={{
					scale: "band"
				}}
				yAxis={yAxis}
				area={areas as any[]}
				bar={bars as IBarProps[]}
				line={lines as any[]}
				colorsPallete={colorPallete}
			/>
		);
	}, [dataChartAccumulated, configCharts]);
	const MemorizedTable = useMemo(() => dataTable && dataTable.length > 0 ? (
		<ListForChartModal 
			data={dataTable} 
			showAccumulatedValues={dataChartAccumulated !== undefined && dataChartAccumulated.length > 0} 
			onClickChartAreaBarLine={onClickChartAreaBarLine} 
		/>
	) : null, [dataTable]);

	return (
		<>
			<RCCGrid container className="indicatorsChartContainer">
				<RCCGrid
					container
					item
					sm={12}
					md={dataChartAccumulated !== undefined && dataChartAccumulated.length > 0 ? 8 : 12}
					className="printIndicatorsChart"
				>
					{MemorizedChart}
				</RCCGrid>
				{dataChartAccumulated !== undefined && dataChartAccumulated.length > 0 && (
					<RCCGrid
						container
						item
						sm={12}
						md={4}
						className="printIndicatorsChartAccumulated"
					>
						{MemorizedChartAccumulated}
					</RCCGrid>
				)}
			</RCCGrid>
			{commentAlert && commentAlert.length > 0 && (
				<FadedDiv>
					{commentAlert.map((comments) => (
						<RCCAlert 
							key={v4()}
							icon={{ name: "comment" }}
							title={comments.name}
							description={comments.text}
						/>
					))}
				</FadedDiv>
			)}
			<RCCGrid 
				container 
				className="printIndicatorsTable"
			>
				{MemorizedTable}
			</RCCGrid>
		</>
	);
};

export default ChartIndicatorsContainer;
